import React, { lazy, Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import componentQueries from 'react-component-queries';
import decode from 'jwt-decode';

import GAListener from './components/GAListener';
import {
  EmptyLayout,
  LayoutUnprotectedRoute,
  AdminProtectedRoute,
  AuthProtectedRoute,
  MainLayout,
} from './components/Layout';
import PageSpinner from './components/PageSpinner';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage/index';
import { store } from './redux';
import { setCurrentUser, addError } from './redux/actions';
import { setToken, call } from './redux/services/api';
import StyleWrap from './styles';
import ErrorBoundary from './components/ErrorBoundary';

const AccountPage = lazy(() => import('./pages/AccountPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PollPage = lazy(() => import('./pages/PollPage'));
const ResultsPage = lazy(() => import('./pages/ResultsPage'));

// Admin Dashboard Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminCandidates = lazy(() => import('./pages/admin/AdminCandidates'));
const AdminControls = lazy(() => import('./pages/admin/AdminControls'));
const AdminEmail = lazy(() => import('./pages/admin/AdminEmail'));

const rehydrateData = async () => {
  try {
    const jwtTokenDecoded = decode(localStorage.jwtToken);
    store.dispatch(setCurrentUser(jwtTokenDecoded));
    const { _id } = jwtTokenDecoded;
    const {
      data: { brownite },
    } = await call('get', `users/${_id}`);
    if (brownite === null || brownite === undefined) {
      store.dispatch(setCurrentUser({}));
      delete localStorage.jwtToken;
    } else {
      store.dispatch(setCurrentUser(brownite));
    }
  } catch (err) {
    delete localStorage.jwtToken;
    store.dispatch(setCurrentUser({}));
    store.dispatch(addError(err));
  }
};

if (localStorage.jwtToken) {
  setToken(localStorage.jwtToken);
  rehydrateData();
}
const App = ({ breakpoint }) => {
  return (
    <Provider store={store}>
      <BrowserRouter>
        {/* <GAListener> */}
        <StyleWrap>
          <Switch>
            <LayoutUnprotectedRoute
              exact
              path="/"
              layout={EmptyLayout}
              component={props => <HomePage {...props} />}
            />
            <LayoutUnprotectedRoute
              exact
              path="/login"
              layout={EmptyLayout}
              component={props => <AuthPage {...props} authState="LOGIN" />}
            />
            <LayoutUnprotectedRoute
              exact
              path="/signup"
              layout={EmptyLayout}
              component={props => <AuthPage {...props} authState="SIGNUP" />}
            />
            <LayoutUnprotectedRoute
              exact
              path="/forgot-password"
              layout={EmptyLayout}
              component={props => (
                <AuthPage {...props} authState="FORGOT_PASSWORD" />
              )}
            />
            <LayoutUnprotectedRoute
              exact
              path="/reset/:resetId"
              layout={EmptyLayout}
              component={props => (
                <AuthPage {...props} authState="RESET_PASSWORD" />
              )}
            />



            <LayoutUnprotectedRoute
              exact
              path="/verify-email"
              layout={EmptyLayout}
              component={VerifyEmail}
            />

            <MainLayout breakpoint={breakpoint}>
              <ErrorBoundary>
                <Suspense fallback={<PageSpinner />}>
                  <Switch>
                    <AuthProtectedRoute
                      exact
                      path="/dashboard"
                      component={DashboardPage}
                    />
                    <AuthProtectedRoute
                      exact
                      path="/account"
                      component={AccountPage}
                    />
                    {/* New Admin Dashboard Routes - exact routes first */}
                    <AdminProtectedRoute
                      exact
                      path="/admin"
                      component={AdminDashboard}
                    />
                    <AdminProtectedRoute
                      exact
                      path="/admin/settings"
                      component={AdminSettings}
                    />
                    <AdminProtectedRoute
                      exact
                      path="/admin/candidates"
                      component={AdminCandidates}
                    />
                    <AdminProtectedRoute
                      exact
                      path="/admin/controls"
                      component={AdminControls}
                    />
                    <AdminProtectedRoute
                      exact
                      path="/admin/email"
                      component={AdminEmail}
                    />
                    {/* User Management - consolidated page */}
                    <AdminProtectedRoute
                      exact
                      path="/admin/users"
                      component={AdminPage}
                    />
                    {/* Parameterized admin route last (legacy - can be removed later) */}
                    <AdminProtectedRoute
                      exact
                      path="/admin/:dept"
                      component={AdminPage}
                    />
                    <AuthProtectedRoute
                      exact
                      path="/elections/:role"
                      component={PollPage}
                    />
                    <AuthProtectedRoute
                      exact
                      path="/results"
                      component={ResultsPage}
                    />
                    <AuthProtectedRoute
                      exact
                      path="/results/:type"
                      component={ResultsPage}
                    />
                  </Switch>
                </Suspense>
              </ErrorBoundary>
              <Redirect from="*" to="/dashboard" />
            </MainLayout>
          </Switch>
        </StyleWrap>
        {/* </GAListener> */}
      </BrowserRouter>
    </Provider>
  );
};
const query = ({ width }) => {
  if (width < 575) {
    return { breakpoint: 'xs' };
  }

  if (576 < width && width < 767) {
    return { breakpoint: 'sm' };
  }

  if (768 < width && width < 991) {
    return { breakpoint: 'md' };
  }

  if (992 < width && width < 1199) {
    return { breakpoint: 'lg' };
  }

  if (width > 1200) {
    return { breakpoint: 'xl' };
  }

  return { breakpoint: 'xs' };
};

export default componentQueries(query)(App);
